import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, UserPlus, Upload, MessageCircle, CheckCircle2, BedDouble } from "lucide-react";
import { BRAND, BED_TYPE, errMsg, inr } from "./constants";

const nowLocal = () => { const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 16); };
const cls = "rounded-xl border-2 px-3 py-2 text-sm w-full";

function GuestPicker({ pid, authFetch, API, guest, setGuest, newGuest, setNewGuest }) {
  const [q, setQ] = useState("");
  const [searched, setSearched] = useState(false);
  const [uploading, setUploading] = useState(false);
  const lookup = async (e) => {
    e.preventDefault();
    try {
      const r = await authFetch({ url: `${API}/homestay/properties/${pid}/guests/lookup?q=${encodeURIComponent(q)}` });
      setGuest(r.data.guest); setSearched(true);
      if (!r.data.guest && /^\d{10}$/.test(q.replace(/\D/g, ""))) setNewGuest({ ...newGuest, mobile: q.replace(/\D/g, "") });
    } catch (err) { toast.error(errMsg(err, "Lookup failed")); }
  };
  const upload = async (file) => {
    const form = new FormData(); form.append("file", file);
    setUploading(true);
    try { const r = await authFetch({ method: "post", url: `${API}/homestay/properties/${pid}/aadhaar`, data: form, headers: { "Content-Type": "multipart/form-data" } }); setNewGuest((g) => ({ ...g, aadhaar_path: r.data.path })); toast.success("Aadhaar uploaded"); }
    catch (err) { toast.error(errMsg(err, "Upload failed")); }
    finally { setUploading(false); }
  };
  return (
    <div data-testid="nb-guest-picker" className="space-y-3">
      <form onSubmit={lookup} className="flex gap-2">
        <input required data-testid="nb-lookup-input" className={cls} placeholder="Mobile or Guest ID (HM-PUN-12345)" value={q} onChange={(e) => setQ(e.target.value)} />
        <button data-testid="nb-lookup-btn" className="rounded-full px-4 text-white shrink-0" style={{ background: BRAND.green }} aria-label="Search guest"><Search className="w-4 h-4" /></button>
      </form>
      {guest && (
        <div data-testid="nb-guest-found" className="rounded-2xl p-3 text-sm" style={{ background: "#F0FDF4" }}>
          <div className="font-bold inline-flex items-center gap-1.5" style={{ color: BRAND.green }}><CheckCircle2 className="w-4 h-4" /> Returning guest · {guest.guest_code}</div>
          <div>{guest.name} · {guest.mobile} · from {guest.city}</div>
          <div className="text-xs" style={{ color: BRAND.muted }}>{guest.has_aadhaar ? "Aadhaar on file — no re-upload needed" : "No Aadhaar on file"}</div>
          <button data-testid="nb-guest-clear" onClick={() => { setGuest(null); setSearched(false); }} className="mt-1 text-xs font-bold" style={{ color: BRAND.orange }}>Change guest</button>
        </div>
      )}
      {!guest && searched && (
        <div data-testid="nb-new-guest" className="rounded-2xl border-2 border-dashed p-3 grid grid-cols-2 gap-2" style={{ borderColor: `${BRAND.orange}66` }}>
          <div className="col-span-2 text-xs font-bold inline-flex items-center gap-1" style={{ color: BRAND.orange }}><UserPlus className="w-3.5 h-3.5" /> New guest — Guest ID is created automatically</div>
          <input data-testid="nb-new-name" className={cls} placeholder="Full name" value={newGuest.name} onChange={(e) => setNewGuest({ ...newGuest, name: e.target.value })} />
          <input data-testid="nb-new-mobile" className={cls} placeholder="Mobile" value={newGuest.mobile} onChange={(e) => setNewGuest({ ...newGuest, mobile: e.target.value })} />
          <input data-testid="nb-new-city" className={cls} placeholder="City they are from" value={newGuest.city} onChange={(e) => setNewGuest({ ...newGuest, city: e.target.value })} />
          <label data-testid="nb-aadhaar-upload" className="rounded-xl border-2 px-3 py-2 text-xs font-bold cursor-pointer inline-flex items-center gap-1.5" style={{ borderColor: newGuest.aadhaar_path ? BRAND.green : BRAND.line, color: BRAND.green }}>
            <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : newGuest.aadhaar_path ? "Aadhaar ✓" : "Upload Aadhaar"}
            <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
          </label>
        </div>
      )}
    </div>
  );
}

function BedPicker({ rooms, bedId, setBedId }) {
  return (
    <div data-testid="nb-bed-picker" className="space-y-3 max-h-[26rem] overflow-y-auto pr-1">
      {rooms.map((rm) => (
        <div key={rm.id}>
          <div className="text-xs font-bold mb-1" style={{ color: BRAND.green }}>{rm.name}{rm.ac ? " · AC" : ""}</div>
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5">
            {rm.beds.map((b) => {
              const free = b.status === "vacant";
              const sel = bedId === b.id;
              return (
                <button key={b.id} type="button" disabled={!free} data-testid={`nb-bed-${b.label}`} onClick={() => setBedId(b.id)}
                  className={`rounded-lg px-1 py-2 text-[10px] font-bold text-white transition-transform ${free && !sel ? "bed-blink" : ""} ${sel ? "scale-105 ring-4 ring-emerald-300" : ""} disabled:opacity-40`}
                  style={{ background: sel ? BRAND.green : free ? "#FF8C1A" : "#9CA3AF" }} title={BED_TYPE[b.bed_type] || ""}>
                  {b.label}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function Money({ p, amount, mode }) {
  const a = +amount || 0;
  const tax = mode === "igst" ? { IGST: Math.round(a * (p.igst_pct ?? 12) / 100) } : mode === "none" ? {} : { CGST: Math.round(a * (p.cgst_pct ?? 6) / 100), SGST: Math.round(a * (p.sgst_pct ?? 6) / 100) };
  const total = a + Object.values(tax).reduce((x, y) => x + y, 0);
  const comm = Math.round(a * (p.commission_pct ?? 0) / 100);
  return (
    <div data-testid="nb-money" className="rounded-xl p-3 text-xs space-y-0.5" style={{ background: "#F6F8F7" }}>
      <div className="flex justify-between"><span>Base</span><b>{inr(a)}</b></div>
      {Object.entries(tax).map(([k, v]) => <div key={k} className="flex justify-between"><span>{k}</span><span>{inr(v)}</span></div>)}
      <div className="flex justify-between text-sm pt-1 border-t" style={{ borderColor: BRAND.line }}><b>Total</b><b data-testid="nb-total" style={{ color: BRAND.green }}>{inr(total)}</b></div>
      <div className="flex justify-between" style={{ color: BRAND.muted }}><span>Commission ({p.commission_pct ?? 0}%)</span><span data-testid="nb-commission">{inr(comm)}</span></div>
    </div>
  );
}

export function NewBookingPanel({ pid, authFetch, API, property }) {
  const [rooms, setRooms] = useState([]);
  const [guest, setGuest] = useState(null);
  const [newGuest, setNewGuest] = useState({ name: "", mobile: "", city: "", aadhaar_path: null });
  const [bedId, setBedId] = useState("");
  const [f, setF] = useState({ check_in: nowLocal(), check_out: "", plan: "nightly", amount_inr: "", tax_mode: "cgst_sgst", source: "walk_in", ota_name: "", payment_status: "pending", status: "checked_in" });
  const [done, setDone] = useState(null);
  const load = useCallback(() => authFetch({ url: `${API}/homestay/properties/${pid}/board` }).then((r) => setRooms(r.data.rooms)).catch(() => {}), [pid, authFetch, API]);
  useEffect(() => { load(); }, [load]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const confirm = async () => {
    try {
      const body = { ...f, amount_inr: +f.amount_inr || 0, bed_id: bedId, check_out: f.check_out || null, ota_name: f.ota_name || null,
        ...(guest ? { guest_id: guest.id } : { new_guest: { ...newGuest, aadhaar_path: newGuest.aadhaar_path || null } }) };
      const r = await authFetch({ method: "post", url: `${API}/homestay/properties/${pid}/front-bookings`, data: body });
      setDone(r.data); toast.success(`Booked ${r.data.booking.bed_label} for ${r.data.guest.name}`);
      setBedId(""); setGuest(null); setNewGuest({ name: "", mobile: "", city: "", aadhaar_path: null }); load();
    } catch (e) { toast.error(errMsg(e, "Booking failed")); }
  };
  const ready = bedId && (guest || (newGuest.name && newGuest.mobile && newGuest.city));

  return (
    <div data-testid="new-booking-panel" className="grid lg:grid-cols-2 gap-4">
      <div className="rounded-2xl border-2 bg-white p-4 space-y-3" style={{ borderColor: BRAND.line }}>
        <div className="text-sm font-bold" style={{ color: BRAND.green }}>1 · Guest</div>
        <GuestPicker pid={pid} authFetch={authFetch} API={API} guest={guest} setGuest={setGuest} newGuest={newGuest} setNewGuest={setNewGuest} />
        <div className="text-sm font-bold pt-2" style={{ color: BRAND.green }}>3 · Stay & payment</div>
        <div className="grid grid-cols-2 gap-2">
          <select data-testid="nb-source" className={cls} value={f.source} onChange={set("source")}><option value="walk_in">Walk-in</option><option value="inquiry">WhatsApp / call</option><option value="ota">OTA / other app</option></select>
          {f.source === "ota" ? <input data-testid="nb-ota-name" className={cls} placeholder="MakeMyTrip, Booking.com…" value={f.ota_name} onChange={set("ota_name")} /> : <select data-testid="nb-plan" className={cls} value={f.plan} onChange={set("plan")}><option value="nightly">Per day</option><option value="monthly">Monthly</option></select>}
          <label className="text-[10px] font-bold uppercase" style={{ color: BRAND.muted }}>Check-in<input type="datetime-local" data-testid="nb-checkin" className={cls} value={f.check_in} onChange={set("check_in")} /></label>
          <label className="text-[10px] font-bold uppercase" style={{ color: BRAND.muted }}>Check-out<input type="date" data-testid="nb-checkout" className={cls} value={f.check_out} onChange={set("check_out")} /></label>
          <input type="number" min={0} data-testid="nb-amount" className={cls} placeholder="Booking amount ₹ (before tax)" value={f.amount_inr} onChange={set("amount_inr")} />
          <select data-testid="nb-tax-mode" className={cls} value={f.tax_mode} onChange={set("tax_mode")}><option value="cgst_sgst">CGST + SGST</option><option value="igst">IGST</option><option value="none">No tax</option></select>
          <select data-testid="nb-payment" className={cls} value={f.payment_status} onChange={set("payment_status")}><option value="pending">Payment pending</option><option value="paid">Paid</option></select>
          <select data-testid="nb-status" className={cls} value={f.status} onChange={set("status")}><option value="checked_in">Check in now</option><option value="confirmed">Reserve (arriving later)</option></select>
        </div>
        <Money p={property} amount={f.amount_inr} mode={f.tax_mode} />
        <button disabled={!ready} data-testid="nb-confirm-btn" onClick={confirm} className="w-full rounded-full py-3 font-bold text-white shadow-lg disabled:opacity-50 transition-transform hover:-translate-y-0.5" style={{ background: BRAND.orange }}>Confirm booking</button>
        {done && (
          <div data-testid="nb-done" className="rounded-2xl p-3 text-xs" style={{ background: "#F0FDF4" }}>
            <pre className="whitespace-pre-wrap font-sans">{done.message}</pre>
            <a href={done.wa_url} target="_blank" rel="noreferrer" data-testid="nb-wa-confirm" className="mt-2 inline-flex items-center gap-1.5 rounded-full px-4 py-2 font-bold text-white" style={{ background: "#16A34A" }}><MessageCircle className="w-4 h-4" /> Send confirmation on WhatsApp</a>
          </div>
        )}
      </div>
      <div className="rounded-2xl border-2 bg-white p-4" style={{ borderColor: BRAND.line }}>
        <div className="text-sm font-bold mb-2 inline-flex items-center gap-1.5" style={{ color: BRAND.green }}><BedDouble className="w-4 h-4" /> 2 · Tap an available bed</div>
        <BedPicker rooms={rooms} bedId={bedId} setBedId={setBedId} />
      </div>
    </div>
  );
}
